import React, { useState } from "react";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
  Timestamp,
  getDoc,
} from "firebase/firestore";
import { db, auth, storage } from "./firebase";
import { ref, uploadBytes } from "firebase/storage";
import { useAdmin } from "./hooks/useAdmin";

// ─── Points formula ────────────────────────────────────────────────────────────
function calculatePoints(place, participants) {
  const raw =
    321 * Math.exp(-0.077 * place) * (0.39 * Math.log(participants) + 0.13);
  return Math.round(raw);
}

function sanitizeId(str) {
  return (str || "")
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\u0370-\u03FF\u1F00-\u1FFF]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

function standardizeCategory(text, discipline) {
  const t = (text || "").toLowerCase();
  let ageGroup = "";

  if (t.includes("juvenile") || t.includes("juveniles")) {
    // Check for II/2 specifically after juvenile keyword
    const lvl = t.match(/juvenile[s]?\s*(2|ii\b)/i);
    ageGroup = lvl ? "Juvenile II" : "Juvenile I";
  } else if (t.includes("junior") || t.includes("juniors")) {
    const lvl = t.match(/junior[s]?\s*(2|ii\b)/i);
    ageGroup = lvl ? "Junior II" : "Junior I";
  } else if (t.includes("youth")) ageGroup = "Youth";
  else if (t.includes("adult")) ageGroup = "Adult";
  else {
    // Fallback: strip age range in parens and 'solo' keyword, take the remainder
    ageGroup = text.replace(/\(\d+[-–]\d+\)/g, "").replace(/\bsolo\b/gi, "").trim();
    // Remove discipline name from ageGroup if present
    if (discipline) {
      const dRe = new RegExp(discipline.replace(/ /g, "\\s+"), "i");
      ageGroup = ageGroup.replace(dRe, "").trim();
    }
  }

  // Build canonical category string
  // For non-Latin disciplines: "Solo {Discipline} {AgeGroup}"
  // For Latin/Standard: "Solo {AgeGroup}"
  if (discipline && discipline !== "Latin" && discipline !== "Standard") {
    return `Solo ${discipline} ${ageGroup}`.replace(/\s+/g, " ").trim();
  }
  return `Solo ${ageGroup}`.replace(/\s+/g, " ").trim();
}

function standardizeDance(text) {
  const t = (text || "").toLowerCase().trim();
  if (t.includes("samba") || t === "sa") return "Samba";
  if (t.includes("cha") || t === "ch") return "ChaChaCha";
  if (t.includes("rumba") || t === "ru") return "Rumba";
  if (t.includes("paso") || t.includes("doble") || t === "pd") return "Pasodoble";
  if (t.includes("jive") || t === "j") return "Jive";
  if (t.includes("vien") && t.includes("waltz")) return "Viennese Waltz";
  if (t.includes("slow") && t.includes("waltz")) return "Slow Waltz";
  if (t.includes("waltz")) return "Slow Waltz";
  if (t.includes("tango")) return "Tango";
  if (t.includes("slow") && (t.includes("fox") || t.includes("foxtrot"))) return "Slow Foxtrot";
  if (t.includes("foxtrot") || t.includes("slowfox")) return "Slow Foxtrot";
  if (t.includes("quickstep")) return "Quickstep";
  if (t.includes("hip") && t.includes("hop")) return "Hip Hop";
  if (t.includes("contemporary")) return "Contemporary";
  if (t.includes("commercial")) return "Commercial";
  return text.trim();
}

function detectDiscipline(danceName) {
  const latin = ["Samba", "ChaChaCha", "Rumba", "Pasodoble", "Jive"];
  const standard = ["Slow Waltz", "Tango", "Viennese Waltz", "Slow Foxtrot", "Quickstep"];
  if (latin.includes(danceName)) return "Latin";
  if (standard.includes(danceName)) return "Standard";
  if (danceName === "Hip Hop") return "Hip Hop";
  if (danceName === "Contemporary") return "Contemporary";
  if (danceName === "Commercial") return "Commercial";
  return "Latin"; // default fallback
}

// ─── Parse a Performing-Arts / criterion-scoring Contaste round ──────────────
// Handles events that use a plain "Summary" section (not "Skating summary")
// with per-judge criterion breakdowns and optionally grade-based placements.
function parsePerformingArtsRound(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const DANCES = "Hip[- ]?Hop|Contemporary|Commercial|Samba|Cha[- ]?Cha[- ]?Cha|Cha[- ]?Cha|Rumba|Paso[- ]?Doble|Pasodoble|Jive|Viennese[- ]?Waltz|Slow[- ]?Waltz|Waltz|Tango|Slow[- ]?Foxtrot|Foxtrot|Quickstep";
  const ROUNDS = "Final|1\\/2\\s*Final|1\\/4\\s*Final|1\\/8\\s*Final|1\\/16\\s*Final|1\\/32\\s*Final|Quarter[- ]?Final|Semi[- ]?Final|1st\\s*Round|2nd\\s*Round|3rd\\s*Round|Round\\s*\\d+|\\d+\\/\\d+\\s*Final|Preliminary";

  // ── 1. Parse competition name + date (scan until a line with date in parens) ──
  let competitionName = "", parsedDate = "";
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed || /^Tolerance:/i.test(trimmed)) continue;
    const m = trimmed.match(/^([^(]+)\(([^)]+)\)/);
    if (m) {
      const dateRaw = m[2].trim();
      const parts = dateRaw.split(/(?<=\d)-(?=\d)/);
      const datePart = parts[parts.length - 1].trim();
      const dotMatch = datePart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      const isoMatch = !dotMatch && datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (dotMatch || isoMatch) {
        competitionName = m[1].replace(/[-–]\s*(Competition|Results?|Report).*/i, "").trim();
        if (dotMatch) {
          const [, dd, mm, yyyy] = dotMatch;
          parsedDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
        } else {
          parsedDate = datePart;
        }
        break;
      }
    }
  }

  // ── 2. Parse category / dance / round ──────────────────────────────────────
  let categoryRaw = "", danceRaw = "", roundLabel = "F";
  for (const line of lines) {
    const reportMatch = line.match(/Detailed marks report[:\s]+(.+)/i);
    if (reportMatch) {
      const titleText = reportMatch[1].trim().replace(/^\d+\.\s*/, "");
      const m = titleText.match(new RegExp(`^(.+?)\\s+(${DANCES})\\s+(${ROUNDS})`, "i"));
      if (m) {
        categoryRaw = m[1].trim();
        danceRaw = m[2].trim();
        const rt = m[3].trim();
        const rtl = rt.toLowerCase().replace(/\s+/g, " ");
        if (/^final$/i.test(rtl)) roundLabel = "F";
        else if (/1\/32/i.test(rtl)) roundLabel = "1/32 Final";
        else if (/1\/16/i.test(rtl)) roundLabel = "1/16 Final";
        else if (/1\/8/i.test(rtl)) roundLabel = "1/8 Final";
        else if (/1\/4|quarter/i.test(rtl)) roundLabel = "1/4 Final";
        else if (/1\/2|semi/i.test(rtl)) roundLabel = "1/2 Final";
        else if (/preliminary/i.test(rtl)) roundLabel = "Preliminary";
        else roundLabel = rt;
      } else {
        // No round label at end – extract dance from middle, combine rest as category
        const m2 = titleText.match(new RegExp(`^(.+?)\\s+(${DANCES})(.*)$`, "i"));
        if (m2) {
          const before = m2[1].trim();
          danceRaw = m2[2].trim();
          const after = m2[3].trim();
          categoryRaw = [before, after].filter(Boolean).join(" ");
        } else {
          categoryRaw = titleText;
        }
        roundLabel = "F";
      }
      break;
    }
  }

  // ── 3. Parse Judges section ────────────────────────────────────────────────
  const adjudicators = [];
  const judgeIds = [];
  const judgeNameToId = new Map();
  let inJudges = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (/^judges?$/i.test(trimmed)) { inJudges = true; continue; }
    if (!inJudges) continue;
    if (!trimmed) { if (adjudicators.length > 0) break; continue; }
    const cells = line.split("\t").map(c => c.trim());
    const [id, name, country = ""] = cells;
    if (/^[A-Z]$/.test(id) && name && name.length > 1 && !/^judge/i.test(name) && id !== "#") {
      adjudicators.push({ id, name, country });
      judgeIds.push(id);
      judgeNameToId.set(name.toLowerCase().trim(), id);
    }
  }

  // ── 4. Parse "Summary" section ─────────────────────────────────────────────
  const isGradePlace = (s) => /^(bronze|silver|gold|platinum)/i.test((s || "").trim());
  let summaryStart = -1;
  for (let i = 0; i < lines.length; i++) {
    if (/^\s*Summary\s*$/i.test(lines[i])) { summaryStart = i; break; }
  }

  const placementMap = new Map(); // number → { place, gradePlacement, name, studio, judgeScores }
  const headerCols = [];
  if (summaryStart !== -1) {
    let i = summaryStart + 1;
    // Collect header lines until the first data row
    while (i < lines.length) {
      const cells = lines[i].split("\t").map(c => c.trim());
      const firstCell = cells[0];
      if (/^\d+$/.test(firstCell) || isGradePlace(firstCell)) break;
      for (const cell of cells) { if (cell !== "") headerCols.push(cell); }
      i++;
    }
    // Map column names to indices
    const numIdx = headerCols.indexOf("#");
    const nameIdx = headerCols.findIndex(h => /^name$/i.test(h));
    const performedIdx = headerCols.findIndex(h => /^performed$/i.test(h));
    const representsIdx = headerCols.findIndex(h => /^represents$/i.test(h));
    const judgeColMap = {}; // judgeId → column index
    for (let ci = 0; ci < headerCols.length; ci++) {
      if (judgeIds.includes(headerCols[ci])) judgeColMap[headerCols[ci]] = ci;
    }
    // Parse data rows
    let rowOrder = 1;
    while (i < lines.length) {
      const rawLine = lines[i]; i++;
      const trimmed = rawLine.trim();
      if (!trimmed) continue;
      const cells = rawLine.split("\t").map(c => c.trim());
      const firstCell = cells[0];
      const isData = /^\d+$/.test(firstCell) || isGradePlace(firstCell);
      if (!isData) {
        if (cells.length === 1 || cells.slice(1).every(c => c === "")) break;
        continue;
      }
      const gradePlacement = isGradePlace(firstCell) ? firstCell : null;
      const numericPlace = gradePlacement ? rowOrder : (parseInt(firstCell) || rowOrder);
      const number = parseInt(cells[numIdx >= 0 ? numIdx : 1]) || 0;
      if (!number) { rowOrder++; continue; }
      const name = nameIdx >= 0 ? (cells[nameIdx] || "") : (cells[(numIdx >= 0 ? numIdx : 1) + 1] || "");
      const studio = representsIdx >= 0 ? (cells[representsIdx] || "") : "";
      const judgeScores = {};
      for (const [jId, ci] of Object.entries(judgeColMap)) {
        const val = parseInt(cells[ci] || "");
        if (!isNaN(val)) judgeScores[jId] = val;
      }
      placementMap.set(number, { place: numericPlace, gradePlacement, name, studio, judgeScores });
      rowOrder++;
    }
  }

  // ── 5. Parse per-judge criterion sections ──────────────────────────────────
  const criteriaMarks = new Map(); // number → { judgeId → { criterion: value } }
  // Scan for judge-name headings; stop before "Results per parameter"
  const judgeSections = [];
  let stopAtLine = lines.length;
  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (/^results per parameter/i.test(trimmed)) { stopAtLine = i; break; }
    const jId = judgeNameToId.get(trimmed.toLowerCase());
    if (jId !== undefined) judgeSections.push({ jId, start: i });
  }
  for (let si = 0; si < judgeSections.length; si++) {
    judgeSections[si].end = si + 1 < judgeSections.length
      ? judgeSections[si + 1].start : stopAtLine;
  }
  for (const { jId, start, end } of judgeSections) {
    const criteria = [];
    let i = start + 1;
    // Skip "Co" rows and blank lines
    while (i < end) {
      const trimmed = lines[i].trim();
      if (!trimmed) { i++; continue; }
      const cells = lines[i].split("\t").map(c => c.trim());
      if (cells[0] === "Co" && cells.slice(1).every(c => c === "")) { i++; continue; }
      break;
    }
    // Collect header criteria (lines before first numeric data row)
    while (i < end) {
      const trimmed = lines[i].trim();
      if (!trimmed) { i++; continue; }
      const cells = lines[i].split("\t").map(c => c.trim());
      if (/^\d+$/.test(cells[0])) break;
      for (const cell of cells) {
        if (cell && cell !== "#" && !/^name$/i.test(cell)) criteria.push(cell);
      }
      i++;
    }
    // Parse data rows
    while (i < end) {
      const cells = lines[i].split("\t").map(c => c.trim()); i++;
      if (!cells[0]) continue;
      const number = parseInt(cells[0]);
      if (isNaN(number)) continue;
      // cells: [number, name, crit1, crit2, ..., total]
      const marks = {};
      for (let ci = 0; ci < criteria.length; ci++) {
        const val = parseInt(cells[ci + 2] || ""); // +2 for number + name
        if (!isNaN(val)) marks[criteria[ci]] = val;
      }
      if (!criteriaMarks.has(number)) criteriaMarks.set(number, {});
      criteriaMarks.get(number)[jId] = marks;
    }
  }

  // ── 6. Assemble competitors ────────────────────────────────────────────────
  const competitors = [];
  for (const [number, info] of placementMap) {
    competitors.push({
      number,
      name: info.name,
      studio: info.studio,
      place: info.place,
      gradePlacement: info.gradePlacement || null,
      judgeMarks: { ...info.judgeScores },
      skatingCols: {},
      judgeRecalls: {},
      totalCrosses: 0,
      isFinal: true,
    });
  }

  return { competitionName, parsedDate, categoryRaw, danceRaw, roundLabel, isFinal: true, adjudicators, judgeIds, competitors };
}

// ─── Parse a single Contaste round ────────────────────────────────────────────
export function parseConstasteRoundHTML(text) {
  const lines = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const rows = lines.map(l => l.split("\t").map(c => c.trim()));

  // Route to the performing-arts parser when the format uses a plain "Summary"
  // section (not the ballroom "Skating summary" / "Marks Summary" style).
  if (lines.some(l => /^\s*Summary\s*$/i.test(l)) &&
      !lines.some(l => /skating\s+summary/i.test(l)) &&
      !lines.some(l => /marks\s+summary/i.test(l))) {
    return parsePerformingArtsRound(text);
  }

  let competitionName = "";
  let parsedDate = "";

  for (const row of rows) {
    const line = row.join(" ").trim();
    if (!line) continue;
    const m = line.match(/^([^(]+)\(([^)]+)\)/);
    if (m) {
      competitionName = m[1]
        .replace(/[-–]\s*(Competition|Results?|Report).*/i, "")
        .trim();
      const dateRaw = m[2].trim();
      const parts = dateRaw.split(/(?<=\d)-(?=\d)/);
      const datePart = parts[parts.length - 1].trim();
      const dotMatch = datePart.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dotMatch) {
        const [, dd, mm, yyyy] = dotMatch;
        parsedDate = `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      } else {
        const isoMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) parsedDate = datePart;
      }
      break;
    }
    break;
  }

  let categoryRaw = "";
  let danceRaw = "";
  let roundLabel = "F";
  let isFinal = false;

  const DANCES = "Hip[- ]?Hop|Contemporary|Commercial|Samba|Cha[- ]?Cha[- ]?Cha|Cha[- ]?Cha|Rumba|Paso[- ]?Doble|Pasodoble|Jive|Viennese[- ]?Waltz|Slow[- ]?Waltz|Waltz|Tango|Slow[- ]?Foxtrot|Foxtrot|Quickstep";
  const ROUNDS = "Final|1\\/2\\s*Final|1\\/4\\s*Final|1\\/8\\s*Final|1\\/16\\s*Final|1\\/32\\s*Final|Quarter[- ]?Final|Semi[- ]?Final|1st\\s*Round|2nd\\s*Round|3rd\\s*Round|Round\\s*\\d+|\\d+\\/\\d+\\s*Final|Preliminary";

  for (const row of rows) {
    const line = row.join("\t");
    const reportMatch = line.match(/Detailed marks report[:\s]+(.+)/i);
    if (reportMatch) {
      const titleText = reportMatch[1].trim().replace(/^\d+\.\s*/, "");
      const m = titleText.match(new RegExp(`^(.+?)\\s+(${DANCES})\\s+(${ROUNDS})`, "i"));
      if (m) {
        categoryRaw = m[1].trim();
        danceRaw = m[2].trim();
        const rt = m[3].trim();
        const rtl = rt.toLowerCase().replace(/\s+/g, " ");
        if (/^final$/i.test(rtl)) { roundLabel = "F"; isFinal = true; }
        else if (/1\/32/i.test(rtl)) roundLabel = "1/32 Final";
        else if (/1\/16/i.test(rtl)) roundLabel = "1/16 Final";
        else if (/1\/8/i.test(rtl)) roundLabel = "1/8 Final";
        else if (/1\/4|quarter/i.test(rtl)) roundLabel = "1/4 Final";
        else if (/1\/2|semi/i.test(rtl)) roundLabel = "1/2 Final";
        else if (/preliminary/i.test(rtl)) roundLabel = "Preliminary";
        else if (/1st/i.test(rtl)) roundLabel = "1";
        else if (/2nd/i.test(rtl)) roundLabel = "2";
        else if (/3rd/i.test(rtl)) roundLabel = "3";
        else {
          // Generic: "Round 4" etc. or "N/M Final"
          const roundNum = rtl.match(/round\s*(\d+)/i);
          const fracFinal = rt.match(/^(\d+\/\d+)\s*Final$/i);
          if (roundNum) roundLabel = `${roundNum[1]}`;
          else if (fracFinal) roundLabel = `${fracFinal[1]} Final`;
          else roundLabel = rt;
        }
      }
      // Fallback: dance name has extra text between it and the round label
      // (e.g. "Solo Cha Cha Cha Juvenile 2 1/2 Final" — "Juvenile 2" sits between dance and round)
      // Strategy: scan for a round label at the END of the title, extract it, then find the dance.
      // IMPORTANT: fractions (1/2 Final etc.) must be listed BEFORE plain "Final" so regex
      // alternation doesn't stop at the bare word "Final" inside "1/2 Final".
      if (!categoryRaw) {
        const DANCES_FB = "Hip[- ]?Hop|Contemporary|Commercial|Samba|Cha[- ]?Cha[- ]?Cha|Cha[- ]?Cha|Rumba|Paso[- ]?Doble|Pasodoble|Jive|Viennese[- ]?Waltz|Slow[- ]?Waltz|Waltz|Tango|Slow[- ]?Foxtrot|Foxtrot|Quickstep";
        const ROUND_END = "1\\/32\\s*Final|1\\/16\\s*Final|1\\/8\\s*Final|1\\/4\\s*Final|Quarter[- ]?Final|1\\/2\\s*Final|Semi[- ]?Final|\\d+\\/\\d+\\s*Final|Final|Preliminary|1st\\s*Round|2nd\\s*Round|3rd\\s*Round|Round\\s*\\d+";
        const roundEndMatch = titleText.match(new RegExp(`\\s+(${ROUND_END})\\s*$`, "i"));
        if (roundEndMatch) {
          const rt2 = roundEndMatch[1].trim();
          const rtl2 = rt2.toLowerCase().replace(/\s+/g, " ");
          if (/^final$/i.test(rtl2)) { roundLabel = "F"; isFinal = true; }
          else if (/1\/32/i.test(rtl2)) roundLabel = "1/32 Final";
          else if (/1\/16/i.test(rtl2)) roundLabel = "1/16 Final";
          else if (/1\/8/i.test(rtl2)) roundLabel = "1/8 Final";
          else if (/1\/4|quarter/i.test(rtl2)) roundLabel = "1/4 Final";
          else if (/1\/2|semi/i.test(rtl2)) roundLabel = "1/2 Final";
          else if (/preliminary/i.test(rtl2)) roundLabel = "Preliminary";
          else if (/1st/i.test(rtl2)) roundLabel = "1";
          else if (/2nd/i.test(rtl2)) roundLabel = "2";
          else if (/3rd/i.test(rtl2)) roundLabel = "3";
          else { const ff = rt2.match(/^(\d+\/\d+)\s*Final$/i); roundLabel = ff ? `${ff[1]} Final` : rt2; }
          if (roundLabel === "F") isFinal = true;
          const titleNoRound = titleText.slice(0, roundEndMatch.index).trim();
          const dm = titleNoRound.match(new RegExp(`(${DANCES_FB})`, "i"));
          if (dm) {
            danceRaw = dm[1];
            categoryRaw = (titleNoRound.slice(0, dm.index) + " " + titleNoRound.slice(dm.index + dm[0].length)).trim().replace(/\s+/g, " ");
          } else {
            categoryRaw = titleNoRound;
          }
        } else {
          // No round label at all (performing-arts style, e.g. "Solo Commercial Juvenile")
          const m2 = titleText.match(new RegExp(`^(.+?)\\s+(${DANCES_FB})(.*)$`, "i"));
          if (m2) {
            danceRaw = m2[2].trim();
            categoryRaw = [m2[1].trim(), m2[3].trim()].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
            roundLabel = "F"; isFinal = true;
          } else {
            categoryRaw = titleText;
          }
        }
      }
      break;
    }
  }

  const sections = [];
  let currentSection = null;

  for (const row of rows) {
    if (row.every(c => c === "")) continue;
    const isSingleCell = row.length === 1 || row.slice(1).every(c => c === "");
    const singleVal = row[0].trim();
    const isHeading = isSingleCell && singleVal.length > 0 && !/^\d+$/.test(singleVal);

    if (isHeading) {
      currentSection = { heading: singleVal, rows: [] };
      sections.push(currentSection);
    } else {
      if (!currentSection) {
        currentSection = { heading: "__header__", rows: [] };
        sections.push(currentSection);
      }
      currentSection.rows.push(row);
    }
  }

  const adjudicators = [];
  const judgeIds = [];

  const judgesSection = sections.find(s => /^judges?$/i.test(s.heading.trim()));
  if (judgesSection) {
    for (const row of judgesSection.rows) {
      if (row.length < 2) continue;
      const id = row[0];
      const name = row[1];
      const country = row[2] || "";
      if (/^judges?$/i.test(name) || /^#$/.test(id)) continue;
      if (/^[A-Z]$/.test(id) && name.length > 1) {
        adjudicators.push({ id, name, country });
        judgeIds.push(id);
      }
    }
  }

  const competitors = [];

  const skatingSection = sections.find(s => /skating\s+summary/i.test(s.heading));
  const marksSection = sections.find(s => /marks\s+summary/i.test(s.heading));
  if (skatingSection) { isFinal = true; roundLabel = roundLabel || "F"; }

  if (isFinal && skatingSection) {
    const placementMap = new Map();
    let colPlace = 0, colNum = 1, colName = 2, colStudio = 3;
    const ssRows = skatingSection.rows;
    if (ssRows.length > 0 && ssRows[0][0] === "#") {
      const hdr = ssRows[0];
      for (let i = 0; i < hdr.length; i++) {
        if (hdr[i] === "№") colNum = i;
        else if (/^name$/i.test(hdr[i])) colName = i;
        else if (/^represents$/i.test(hdr[i])) colStudio = i;
      }
    }
    for (const row of ssRows) {
      if (row[0] === "#") continue;
      const place = parseInt(row[colPlace]);
      const number = parseInt(row[colNum]);
      const name = row[colName] || "";
      const studio = row[colStudio] || "";
      if (!isNaN(place) && !isNaN(number) && name.length > 1) {
        placementMap.set(number, { place, name, studio });
      }
    }

    for (const sec of sections) {
      if (sec === skatingSection) continue;
      const headingIsDance = sec.heading.length <= 40
        && /^(hip[\s-]?hop|contemporary|commercial|samba|cha[\s-]?cha[\s-]?cha|cha.?cha|rumba|paso.?doble|pasodoble|jive|viennese waltz|slow waltz|waltz|tango|slow foxtrot|foxtrot|quickstep)$/i.test(sec.heading.trim());
      const hasJudgeRow = judgeIds.length > 0
        && sec.rows.some(row => judgeIds.some(jid => row.includes(jid)));
      if (!headingIsDance && !hasJudgeRow) continue;

      let headerIdx = -1;
      let marksHeader = [];
      for (let i = 0; i < sec.rows.length; i++) {
        const row = sec.rows[i];
        if (judgeIds.length > 0
          ? judgeIds.some(jid => row.includes(jid))
          : row.filter(c => /^[A-Z]$/.test(c)).length >= 2) {
          headerIdx = i;
          marksHeader = row;
          break;
        }
      }
      if (headerIdx === -1) continue;

      let numCol = 0;
      const colJudges = [];
      const colSkating = [];
      for (let ci = 0; ci < marksHeader.length; ci++) {
        const h = marksHeader[ci];
        if (h === "№") { numCol = ci; continue; }
        if (h === "#") continue;
        if (judgeIds.length > 0 ? judgeIds.includes(h) : /^[A-Z]$/.test(h)) {
          colJudges.push({ jId: h, ci });
        }
        if (/^\d+-\d+$/.test(h)) colSkating.push({ key: h, ci });
      }

      for (let i = headerIdx + 1; i < sec.rows.length; i++) {
        const row = sec.rows[i];
        const number = parseInt(row[numCol]);
        if (isNaN(number)) continue;
        const judgeMarks = {};
        for (const { jId, ci } of colJudges) {
          const val = (row[ci] || "").trim();
          const num = parseInt(val);
          judgeMarks[jId] = isNaN(num) ? null : num;
        }
        const skatingCols = {};
        for (const { key, ci } of colSkating) {
          skatingCols[key] = (row[ci] || "").trim();
        }
        const info = placementMap.get(number);
        let comp = competitors.find(c => c.number === number);
        if (!comp) {
          comp = { number, name: info?.name || "", studio: info?.studio || "", place: info?.place ?? 999, judgeMarks, skatingCols, judgeRecalls: {}, totalCrosses: 0, isFinal: true };
          competitors.push(comp);
        } else {
          Object.assign(comp.judgeMarks, judgeMarks);
          Object.assign(comp.skatingCols, skatingCols);
          if (!comp.name && info?.name) comp.name = info.name;
          if (!comp.studio && info?.studio) comp.studio = info.studio;
          if (comp.place === 999 && info?.place) comp.place = info.place;
        }
      }
    }

    for (const [number, info] of placementMap) {
      if (!competitors.find(c => c.number === number)) {
        competitors.push({ number, name: info.name, studio: info.studio, place: info.place, judgeMarks: {}, skatingCols: {}, judgeRecalls: {}, totalCrosses: 0, isFinal: true });
      }
    }

  } else if (marksSection) {
    const recallMap = new Map();
    let colPlace = 0, colNum = 1, colName = 2, colStudio = 3, colR = 4, colRecall = 5;
    const msRows = marksSection.rows;
    if (msRows.length > 0 && msRows[0][0] === "#") {
      const hdr = msRows[0];
      for (let i = 0; i < hdr.length; i++) {
        if (hdr[i] === "№") colNum = i;
        else if (/^name$/i.test(hdr[i])) colName = i;
        else if (/^represents$/i.test(hdr[i])) colStudio = i;
        else if (/^r$/i.test(hdr[i])) colR = i;
        else if (/^recall$/i.test(hdr[i])) colRecall = i;
      }
    }
    for (const row of msRows) {
      if (row[0] === "#") continue;
      const placeStr = row[colPlace] || "";
      const number = parseInt(row[colNum]);
      const name = row[colName] || "";
      const studio = row[colStudio] || "";
      const crossCount = parseInt(row[colR]) || 0;
      const recalled = /v|x|✓|yes/i.test(row[colRecall] || "");
      if (isNaN(number) || name.length < 2) continue;
      const place = parseInt(placeStr.split(/[-–]/)[0]) || 999;
      recallMap.set(number, { place, name, studio, totalCrosses: crossCount, recalled });
    }

    for (const sec of sections) {
      if (sec === marksSection) continue;
      const headingIsDance = /hip[\s-]?hop|contemporary|commercial|samba|cha.?cha|rumba|paso|jive|waltz|tango|slow foxtrot|foxtrot|quickstep/i.test(sec.heading);
      if (!headingIsDance && !sec.rows.some(r => judgeIds.some(jid => r.includes(jid)))) continue;

      let headerIdx = -1;
      let marksHeader = [];
      for (let i = 0; i < sec.rows.length; i++) {
        const row = sec.rows[i];
        if (judgeIds.length > 0
          ? judgeIds.some(jid => row.includes(jid))
          : row.filter(c => /^[A-Z]$/.test(c)).length >= 2) {
          headerIdx = i;
          marksHeader = row;
          break;
        }
      }
      if (headerIdx === -1) continue;

      let numCol = 0;
      const colJudges = [];
      for (let ci = 0; ci < marksHeader.length; ci++) {
        const h = marksHeader[ci];
        if (h === "№") { numCol = ci; continue; }
        if (h === "#") continue;
        if (judgeIds.length > 0 ? judgeIds.includes(h) : /^[A-Z]$/.test(h)) {
          colJudges.push({ jId: h, ci });
        }
      }

      for (let i = headerIdx + 1; i < sec.rows.length; i++) {
        const row = sec.rows[i];
        const number = parseInt(row[numCol]);
        if (isNaN(number)) continue;
        const judgeRecalls = {};
        let crossCount = 0;
        for (const { jId, ci } of colJudges) {
          const val = (row[ci] || "").trim().toLowerCase();
          const recalled = val === "x" || val === "v" || val === "✓";
          judgeRecalls[jId] = recalled;
          if (recalled) crossCount++;
        }
        const info = recallMap.get(number);
        let comp = competitors.find(c => c.number === number);
        if (!comp) {
          comp = { number, name: info?.name || "", studio: info?.studio || "", place: info?.place ?? 999, judgeMarks: {}, skatingCols: {}, judgeRecalls, totalCrosses: crossCount, recalled: info?.recalled ?? (crossCount > Math.floor(judgeIds.length / 2)), isFinal: false };
          competitors.push(comp);
        } else {
          Object.assign(comp.judgeRecalls, judgeRecalls);
          comp.totalCrosses = crossCount;
        }
      }
    }

    for (const [number, info] of recallMap) {
      if (!competitors.find(c => c.number === number)) {
        competitors.push({ number, name: info.name, studio: info.studio, place: info.place, judgeMarks: {}, skatingCols: {}, judgeRecalls: {}, totalCrosses: info.totalCrosses, recalled: info.recalled, isFinal: false });
      }
    }
  }

  return { competitionName, parsedDate, categoryRaw, danceRaw, roundLabel, isFinal, adjudicators, judgeIds, competitors };
}

// ─── Merge multiple parsed rounds ─────────────────────────────────────────────
function mergeRounds(parsedRounds, overrideName, overrideCategory, overrideDance, dateStr, multiplier) {
  if (parsedRounds.length === 0) return null;

  const firstWithMeta = parsedRounds.find(r => r.categoryRaw || r.danceRaw) || parsedRounds[0];
  const rawCategory = overrideCategory || firstWithMeta.categoryRaw || "";
  const rawDance = overrideDance || firstWithMeta.danceRaw || "";
  const dance = standardizeDance(rawDance);
  const discipline = detectDiscipline(dance);
  const category = standardizeCategory(rawCategory, discipline);
  const competitionName = overrideName || parsedRounds[0].competitionName || "Competition";
  const effectiveDate = dateStr || parsedRounds.find(r => r.parsedDate)?.parsedDate || "";

  const adjMap = new Map();
  const judgeIdOrder = [];
  for (const r of parsedRounds) {
    for (const a of r.adjudicators) {
      if (!adjMap.has(a.id)) { adjMap.set(a.id, a); judgeIdOrder.push(a.id); }
    }
  }
  const adjudicators = judgeIdOrder.map(id => adjMap.get(id));
  const judgeIds = judgeIdOrder;

  const byNumber = new Map();
  for (const roundData of parsedRounds) {
    for (const comp of roundData.competitors) {
      if (!byNumber.has(comp.number)) {
        byNumber.set(comp.number, { number: comp.number, name: comp.name, studio: comp.studio, rounds: [] });
      }
      const entry = byNumber.get(comp.number);
      if (!entry.name && comp.name) entry.name = comp.name;
      if (!entry.studio && comp.studio) entry.studio = comp.studio;
      const danceKey = dance || "Dance";
      const hasSkatingCols = Object.keys(comp.skatingCols || {}).length > 0;
      const danceData = {
        [danceKey]: { type: roundData.isFinal ? "final" : "preliminary", isSkating: roundData.isFinal && hasSkatingCols, judgeMarks: comp.judgeMarks || {}, judgeRecalls: comp.judgeRecalls || {}, skatingCols: comp.skatingCols || {}, totalCrosses: comp.totalCrosses || 0, sum: null },
      };
      // Compute ∑ only for scoring (non-skating) finals
      if (!hasSkatingCols) {
        const jmVals = Object.values(comp.judgeMarks || {}).filter(v => v != null);
        const computedSum = jmVals.length > 0 ? jmVals.reduce((a, b) => a + b, 0) : null;
        const dKey = dance || "Dance";
        if (danceData[dKey]) danceData[dKey].sum = computedSum;
      }
      const alreadyHas = entry.rounds.some(r => r.round === roundData.roundLabel);
      if (!alreadyHas) {
        entry.rounds.push({ round: roundData.roundLabel, rank: comp.place, danceData });
      }
    }
  }

  function roundOrder(r) {
    if (r === "F") return 10000;
    if (r === "Preliminary") return -1;
    const frac = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 };
    if (frac[r] != null) return frac[r];
    const n = parseInt(r);
    return isNaN(n) ? 5 : n;
  }

  const results = [];
  const totalParticipants = byNumber.size;

  // ── Collect all round labels and sort them earliest→latest ───────────────
  const allRoundLabelsSet = new Set();
  for (const [, entry] of byNumber) {
    for (const rd of entry.rounds) allRoundLabelsSet.add(rd.round);
  }
  // Sorted ascending: earliest first (Preliminary < 1/4 Final < 1/2 Final < F)
  const allRoundsSorted = [...allRoundLabelsSet].sort((a, b) => roundOrder(a) - roundOrder(b));

  // ── Detect star dancers: those missing from the earliest round ───────────
  const earliestRound = allRoundsSorted[0] ?? null;
  const starNumbers = new Set();
  if (allRoundsSorted.length > 1 && earliestRound) {
    for (const [, entry] of byNumber) {
      if (!entry.rounds.some(rd => rd.round === earliestRound)) {
        starNumbers.add(entry.number);
      }
    }
  }
  const starCount = starNumbers.size;

  // ── Sort each dancer's rounds and determine their last (best) round ───────
  for (const [, entry] of byNumber) {
    entry.rounds.sort((a, b) => roundOrder(a.round) - roundOrder(b.round));
  }

  // ── Build a map: roundLabel → entries eliminated there ───────────────────
  // A dancer is "eliminated" in a round if that is their last (best) round.
  // Note: entries recalled to the next round are NOT in this group for that round.
  const eliminatedByRound = new Map(); // roundLabel → [{entry, contesteRank, crosses}]
  for (const [, entry] of byNumber) {
    const bestRound = entry.rounds[entry.rounds.length - 1];
    const rd = bestRound;
    const contesteRank = rd.rank ?? 999; // from Contaste marks summary
    // Sum of crosses across all dances in this round (for tiebreaking)
    let crosses = 0;
    for (const [, dd] of Object.entries(rd.danceData || {})) {
      crosses += dd.totalCrosses || 0;
    }
    if (!eliminatedByRound.has(rd.round)) eliminatedByRound.set(rd.round, []);
    eliminatedByRound.get(rd.round).push({ entry, contesteRank, crosses });
  }

  // ── Assign global consecutive places group by group, latest round first ──
  // Process rounds from latest (Final) to earliest, assigning places in order.
  let gi = 1;
  for (let ri = allRoundsSorted.length - 1; ri >= 0; ri--) {
    const roundLabel = allRoundsSorted[ri];
    const group = eliminatedByRound.get(roundLabel) || [];
    if (group.length === 0) continue;

    // For the earliest round: add starCount to non-star ranks because Contaste
    // ranked that round without counting the stars (who entered later).
    const isEarliestRound = (roundLabel === earliestRound) && starCount > 0;

    // Sort group by Contaste rank (already adjusted below), then by crosses desc
    // For Final: contesteRank IS the correct final placement (skating result).
    // For prelim rounds: contesteRank from the marks summary; for the earliest
    // round, non-stars get +starCount so their adjusted rank pushes them back.
    group.sort((a, b) => {
      const aRank = isEarliestRound && !starNumbers.has(a.entry.number)
        ? a.contesteRank + starCount : a.contesteRank;
      const bRank = isEarliestRound && !starNumbers.has(b.entry.number)
        ? b.contesteRank + starCount : b.contesteRank;
      if (aRank !== bRank) return aRank - bRank;
      return b.crosses - a.crosses; // more crosses = better within same rank
    });

    // Assign global places with tie detection (same adjusted rank = tie)
    let k = 0;
    while (k < group.length) {
      const aRankK = isEarliestRound && !starNumbers.has(group[k].entry.number)
        ? group[k].contesteRank + starCount : group[k].contesteRank;
      let m = k + 1;
      while (m < group.length) {
        const aRankM = isEarliestRound && !starNumbers.has(group[m].entry.number)
          ? group[m].contesteRank + starCount : group[m].contesteRank;
        if (aRankM !== aRankK) break;
        m++;
      }
      // [k..m) all share the same adjusted rank → same global place
      for (let q = k; q < m; q++) {
        const isStar = starNumbers.has(group[q].entry.number);
        const globalPlace = gi;
        const points = calculatePoints(globalPlace, totalParticipants);
        // Also update the round's rank field so roundData in Firestore is correct
        const bestRdObj = group[q].entry.rounds[group[q].entry.rounds.length - 1];
        bestRdObj.rank = globalPlace;
        results.push({
          dancerName: group[q].entry.name,
          studio: group[q].entry.studio,
          place: globalPlace,
          points,
          number: group[q].entry.number,
          highestRound: bestRdObj.round,
          isStar,
          rounds: group[q].entry.rounds,
        });
      }
      gi += (m - k);
      k = m;
    }
  }

  let timestamp = null;
  if (effectiveDate) {
    const d = new Date(effectiveDate + "T00:00:00Z");
    if (!isNaN(d.getTime())) timestamp = Timestamp.fromDate(d);
  }

  const docId = [
    sanitizeId(competitionName).substring(0, 50),
    sanitizeId(category),
    dance ? sanitizeId(dance) : "",
  ].filter(Boolean).join("__");

  return { id: docId, name: competitionName, category, discipline, dance, adjudicators, judgeIds, dances: dance ? [dance] : [], timestamp, participants: totalParticipants, multiplier: parseFloat(multiplier) || 1, results, source: "ConstasteNew" };
}

// ─── React Component ──────────────────────────────────────────────────────────
export default function UploadConstasteNew() {
  const { isAdmin, loading: adminLoading } = useAdmin();

  const [roundHtmls, setRoundHtmls] = useState([{ id: 0, label: "", html: "" }]);
  const [parsedRounds, setParsedRounds] = useState([]);
  const [mergedDoc, setMergedDoc] = useState(null);
  const [jsonPreview, setJsonPreview] = useState("");

  const [compName, setCompName] = useState("");
  const [competitionDate, setCompetitionDate] = useState("");
  const [overrideCategory, setOverrideCategory] = useState("");
  const [overrideDance, setOverrideDance] = useState("");
  const [multiplier, setMultiplier] = useState("1");
  const [uploadStatus, setUploadStatus] = useState("");

  const [hoveredJudge, setHoveredJudge] = useState(null);
  const [selectedJudges, setSelectedJudges] = useState(new Set());
  const toggleJudge = (jId) => setSelectedJudges(prev => {
    const next = new Set(prev);
    next.has(jId) ? next.delete(jId) : next.add(jId);
    return next;
  });

  if (adminLoading) {
    return <div style={{ padding: "2rem", textAlign: "center" }}><h2>🎭 Upload Contaste New</h2><p>Loading...</p></div>;
  }
  if (!isAdmin) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <h2>🎭 Upload Contaste New</h2>
        <div style={{ padding: "2rem", border: "1px solid #f5c6cb", borderRadius: "4px", backgroundColor: "#f8d7da", color: "#721c24", marginTop: "2rem" }}>
          <h3>🔒 Access Denied</h3>
          <p>You need administrator privileges to access this page.</p>
        </div>
      </div>
    );
  }

  const addRound = () => setRoundHtmls(prev => [...prev, { id: Date.now(), label: "", html: "" }]);
  const removeRound = (id) => setRoundHtmls(prev => prev.filter(r => r.id !== id));
  const updateRound = (id, field, value) => setRoundHtmls(prev => prev.map(r => r.id === id ? { ...r, [field]: value } : r));

  const handleParse = () => {
    try {
      const rounds = [];
      const logs = [];
      for (const r of roundHtmls) {
        if (!r.html.trim()) continue;
        const parsed = parseConstasteRoundHTML(r.html);
        parsed._userLabel = r.label || null;
        rounds.push(parsed);
        logs.push(`✅ Round "${parsed.roundLabel}" (${parsed.isFinal ? "Final" : "Prelim"}) — ${parsed.competitors.length} competitors, ${parsed.adjudicators.length} judges, dance: "${parsed.danceRaw}", category: "${parsed.categoryRaw}"`);
      }
      if (rounds.length === 0) {
        setUploadStatus("❌ No content found. Paste at least one round.");
        return;
      }
      setParsedRounds(rounds);

      const parsedName = rounds.find(r => r.competitionName)?.competitionName || "";
      const parsedDate = rounds.find(r => r.parsedDate)?.parsedDate || "";
      if (!compName && parsedName) {
        const year = parsedDate ? parsedDate.substring(0, 4) : "";
        const nameWithYear = year && !parsedName.includes(year) ? `${parsedName} ${year}` : parsedName;
        setCompName(nameWithYear);
      }
      if (!competitionDate && parsedDate) setCompetitionDate(parsedDate);

      const merged = mergeRounds(rounds, compName || parsedName, overrideCategory, overrideDance, competitionDate || parsedDate, multiplier);
      setMergedDoc(merged);
      setJsonPreview(JSON.stringify(merged, null, 2));
      setUploadStatus("✅ Parsed:\n" + logs.join("\n") + `\n\n→ Merged: ${merged.participants} competitors, doc ID: ${merged.id}${parsedName ? `\n→ Competition: ${parsedName}` : ""}${parsedDate ? `\n→ Date: ${parsedDate}` : ""}`);
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Parse error: " + err.message);
    }
  };

  const handleUpload = async () => {
    try {
      if (!auth.currentUser) throw new Error("You must be signed in");
      if (!jsonPreview.trim()) throw new Error("Parse the rounds first");
      if (!competitionDate) throw new Error("Competition date is required");

      const data = JSON.parse(jsonPreview);
      if (!data.results || data.results.length === 0) throw new Error("No results found");

      const d = new Date(competitionDate + "T00:00:00Z");
      data.timestamp = Timestamp.fromDate(d);
      data.multiplier = parseFloat(multiplier) || 1;
      if (compName) data.name = compName;

      data.results = data.results.map(r => ({
        ...r,
        points: calculatePoints(r.place, data.participants),
      }));

      const docId = data.id;
      const docRef = doc(db, "competitions", docId);

      // Check for duplicate
      setUploadStatus("⏳ Checking for duplicates...");
      const existingDoc = await getDoc(docRef);
      if (existingDoc.exists()) {
        const existing = existingDoc.data();
        const shouldOverwrite = window.confirm(
          `A competition with this ID already exists:\n\nExisting: "${existing.name}"\nNew: "${data.name}"\n\nOverwrite?`
        );
        if (!shouldOverwrite) {
          setUploadStatus("❌ Upload cancelled — duplicate detected");
          return;
        }
      }

      setUploadStatus("⏳ Uploading to Firebase...");

      const competitionDoc = {
        id: docId,
        name: data.name,
        discipline: data.discipline,
        dance: data.dance,
        category: data.category,
        timestamp: data.timestamp,
        uploadedAt: serverTimestamp(),
        multiplier: data.multiplier,
        participants: data.participants,
        // Only the fields that Leaderboard and ranking calculations read
        results: data.results.map(r => ({
          dancerName: r.dancerName,
          studio: r.studio || "",
          place: r.place,
          points: r.points,
        })),
        // Full round/judge data stored separately — not read by Leaderboard
        roundData: data.results.map(r => ({
          dancerName: r.dancerName,
          number: r.number || null,
          highestRound: r.highestRound || null,
          rounds: r.rounds || [],
        })),
        adjudicators: data.adjudicators || [],
        judgeIds: data.judgeIds || [],
        dances: data.dances || (data.dance ? [data.dance] : []),
        source: "ConstasteNew",
      };

      await setDoc(docRef, competitionDoc);

      // Storage backup
      try {
        const storageName = (data.name || "competition").replace(/[\\/:*?"<>|#\[\]]/g, "_").trim();
        const storageCat = (data.category || "category").replace(/[\\/:*?"<>|#\[\]]/g, "_").trim();
        const storageDance = data.dance ? `_${data.dance.replace(/[\\/:*?"<>|#\[\]]/g, "_")}` : "";
        const storageRef = ref(storage, `competition_results/${storageName}/${storageCat}${storageDance}.json`);
        const jsonBlob = new Blob([JSON.stringify(competitionDoc, null, 2)], { type: "application/json" });
        await uploadBytes(storageRef, jsonBlob);
      } catch (storageErr) {
        console.warn("Storage backup failed:", storageErr.message);
      }

      // Create dancer docs
      for (const result of data.results || []) {
        if (!result.dancerName) continue;
        await setDoc(doc(db, "dancers", result.dancerName), {
          name: result.dancerName,
          studio: result.studio || "",
          createdAt: serverTimestamp(),
        }, { merge: true });
      }

      await addDoc(collection(db, "competitionLogs"), {
        uploadedBy: auth.currentUser?.email || "Unknown",
        timestamp: serverTimestamp(),
        summary: `${data.name} (${data.participants} participants)`,
        documentPath: `competitions/${docId}`,
        source: "ConstasteNew",
      });

      setUploadStatus(`✅ Upload successful! Document stored at: competitions/${docId}`);
    } catch (err) {
      console.error(err);
      setUploadStatus("❌ Upload failed: " + err.message);
    }
  };

  return (
    <div className="upload-container">
      <h2>🎭 Upload Contaste New</h2>
      <p style={{ color: "#666", fontSize: "0.9rem", marginBottom: "1.5rem" }}>
        Paste the text content (copy–paste from browser) for each round of a single category/dance event.
        All rounds will be merged into one competition document with full judge marks and recall data.
      </p>

      {/* ── Competition metadata ─────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label>🏆 Competition Name (override parsed name):</label>
          <input type="text" placeholder="e.g. Ioannina Dance Festival 2026" value={compName} onChange={e => setCompName(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>📅 Competition Date:</label>
          <input type="date" required value={competitionDate} onChange={e => setCompetitionDate(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>🏷️ Category override (optional):</label>
          <input type="text" placeholder="e.g. Juvenile I" value={overrideCategory} onChange={e => setOverrideCategory(e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label>💃 Dance override (optional):</label>
          <input type="text" placeholder="e.g. Rumba" value={overrideDance} onChange={e => setOverrideDance(e.target.value)} />
        </div>
      </div>

      <div className="form-group" style={{ maxWidth: "200px" }}>
        <label>🧮 Multiplier (default 1):</label>
        <input type="number" step="0.1" min="0.1" className="number-input" value={multiplier} onChange={e => setMultiplier(e.target.value)} />
      </div>

      {/* ── Round inputs ─────────────────────────────────────────────────── */}
      <div style={{ marginTop: "1.5rem" }}>
        <h3 style={{ marginBottom: "0.75rem" }}>📄 Round Text</h3>
        <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "1rem" }}>
          Add one block per round (e.g. "1/2 Final" and "Final"). Copy–paste the text directly from the Contaste report page.
        </p>

        {roundHtmls.map((r, idx) => (
          <div key={r.id} style={{ border: "1px solid #ddd", borderRadius: "8px", padding: "1rem", marginBottom: "1rem", backgroundColor: "#fafafa" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.5rem" }}>
              <strong>Round {idx + 1}</strong>
              <input
                type="text"
                placeholder="Label (optional, e.g. '1/2 Final')"
                value={r.label}
                onChange={e => updateRound(r.id, "label", e.target.value)}
                style={{ flex: 1, padding: "4px 8px", borderRadius: "4px", border: "1px solid #ccc" }}
              />
              {roundHtmls.length > 1 && (
                <button onClick={() => removeRound(r.id)} style={{ background: "#dc3545", color: "white", border: "none", borderRadius: "4px", padding: "4px 10px", cursor: "pointer" }}>
                  ✕ Remove
                </button>
              )}
            </div>
            <textarea
              rows={8}
              className="code-textarea"
              value={r.html}
              onChange={e => updateRound(r.id, "html", e.target.value)}
              placeholder="Copy the text from the Contaste detailed marks report page and paste it here (Ctrl+A, Ctrl+C on the report page, then paste)…"
              style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8rem" }}
            />
          </div>
        ))}

        <button onClick={addRound} style={{ background: "none", border: "2px dashed #aaa", borderRadius: "8px", padding: "8px 20px", cursor: "pointer", color: "#666", fontSize: "0.9rem", width: "100%", marginBottom: "1rem" }}>
          + Add Another Round
        </button>
      </div>

      <button className="btn btn-primary" onClick={handleParse} style={{ marginRight: "1rem" }}>
        🔍 Parse All Rounds
      </button>

      {/* ── Preview ──────────────────────────────────────────────────────── */}
      {mergedDoc && (
        <div style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>📋 Parsed Data Preview</h3>

          <div style={{ background: "#f0f8ff", border: "1px solid #b8d4e8", borderRadius: "6px", padding: "1rem", marginBottom: "1rem", fontSize: "0.92rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.4rem 1.5rem" }}>
              <div><strong>Competition:</strong> {mergedDoc.name}</div>
              <div><strong>Category:</strong> {mergedDoc.category}</div>
              <div><strong>Discipline:</strong> {mergedDoc.discipline}</div>
              <div><strong>Dance:</strong> {mergedDoc.dance}</div>
              <div><strong>Participants:</strong> {mergedDoc.participants}</div>
              <div><strong>Rounds:</strong> {parsedRounds.map(r => r.roundLabel === "F" ? "Final" : r.roundLabel).join(", ")}</div>
              <div style={{ gridColumn: "1 / -1" }}>
                <strong>Adjudicators:</strong>{" "}
                <span style={{ display: "inline-flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
                  {(mergedDoc.adjudicators || []).map(a => {
                    const isSelected = selectedJudges.has(a.id);
                    const isHov = hoveredJudge === a.id;
                    const active = isSelected || isHov;
                    return (
                      <span key={a.id} onMouseEnter={() => setHoveredJudge(a.id)} onMouseLeave={() => setHoveredJudge(null)} onClick={() => toggleJudge(a.id)}
                        style={{ display: "inline-flex", alignItems: "center", gap: "4px", padding: "2px 8px", borderRadius: "12px", cursor: "pointer", fontSize: "0.85rem", transition: "all 0.15s", userSelect: "none", background: isSelected ? "#d0e8ff" : isHov ? "#ffe066" : "#e8eaf6", color: active ? "#222" : "#333", fontWeight: active ? "bold" : "normal", boxShadow: isSelected ? "0 0 0 2px #2979ff" : isHov ? "0 0 0 2px #f5c400" : "none", border: "1px solid " + (isSelected ? "#2979ff" : isHov ? "#f5c400" : "#c5cae9") }}>
                        <span style={{ fontWeight: "bold", fontSize: "0.78rem", background: isSelected ? "#2979ff" : isHov ? "#f5c400" : "#3a3a5c", color: isSelected ? "#fff" : isHov ? "#222" : "#fff", borderRadius: "8px", padding: "0 5px", minWidth: "18px", textAlign: "center", display: "inline-block", transition: "all 0.15s" }}>{a.id}</span>
                        {a.name}{a.country ? ` (${a.country})` : ""}
                      </span>
                    );
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Results table */}
          <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "0.4rem" }}>
            <h4 style={{ margin: 0 }}>
              {(() => {
                // Detect star dancers: those missing from the earliest round when multiple rounds exist
                const allRoundLabels = new Set();
                for (const r of (mergedDoc.results || [])) {
                  for (const rd of (r.rounds || [])) allRoundLabels.add(rd.round);
                }
                if (allRoundLabels.size <= 1) return `Results (${mergedDoc.participants} participants)`;
                function rsk(r) {
                  if (r === "F") return 10000;
                  if (r === "Preliminary") return -1;
                  const frac = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 };
                  if (frac[r] != null) return frac[r];
                  const n = parseInt(r); return isNaN(n) ? 5 : n;
                }
                const earliest = [...allRoundLabels].sort((a, b) => rsk(a) - rsk(b))[0];
                const starCount = (mergedDoc.results || []).filter(r => !(r.rounds || []).some(rd => rd.round === earliest)).length;
                return `Results (${mergedDoc.participants} participants${starCount > 0 ? ` · Stars: ${starCount}` : ""})`;
              })()}
            </h4>
            {selectedJudges.size > 0 && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "0.85rem", color: "#2979ff" }}>
                <strong>{selectedJudges.size}</strong> judge{selectedJudges.size > 1 ? "s" : ""} selected
                <button onClick={() => setSelectedJudges(new Set())} style={{ background: "none", border: "1px solid #2979ff", color: "#2979ff", borderRadius: "8px", padding: "1px 8px", cursor: "pointer", fontSize: "0.8rem", fontWeight: "bold" }}>✕ Clear</button>
              </span>
            )}
          </div>

          <div style={{ overflowX: "auto", marginBottom: "1.5rem", width: "100%", display: "block" }}>
            <table style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr style={{ background: "#e8e8e8" }}>
                  <th style={thStyle}>Rank</th>
                  <th style={thStyle}>No.</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Studio</th>
                  <th style={thStyle}>Rnd.</th>
                  {(mergedDoc.dances || []).map(d => (
                    <React.Fragment key={d}>
                      <th style={{ ...thStyle, background: "#3a3a5c", color: "#fff" }}>
                        {d}<br />
                        <span style={{ fontWeight: "normal", fontSize: "0.76rem", fontFamily: "monospace", display: "inline-flex", gap: 0 }}>
                          {(mergedDoc.judgeIds || []).map(jId => {
                            const adj = (mergedDoc.adjudicators || []).find(a => a.id === jId);
                            const isSelected = selectedJudges.has(jId);
                            const isHov = hoveredJudge === jId;
                            return (
                              <span key={jId} title={adj ? `${adj.id}. ${adj.name}${adj.country ? ` (${adj.country})` : ""}` : jId} onMouseEnter={() => setHoveredJudge(jId)} onMouseLeave={() => setHoveredJudge(null)} onClick={() => toggleJudge(jId)}
                                style={{ cursor: "pointer", width: "1.6ch", display: "inline-block", textAlign: "center", borderRadius: "2px", userSelect: "none", color: isSelected ? "#7ec8ff" : isHov ? "#ffe066" : "#fff", background: isSelected ? "rgba(41,121,255,0.25)" : isHov ? "rgba(255,224,102,0.18)" : "transparent", fontWeight: (isSelected || isHov) ? "bold" : "normal", transition: "all 0.1s" }}>
                                {jId}
                              </span>
                            );
                          })}
                        </span>
                      </th>
                      <th style={{ ...thStyle, textAlign: "center", padding: "2px 4px", background: "#3a3a5c", color: "#fff" }}>S</th>
                    </React.Fragment>
                  ))}
                  <th style={thStyle}>Points</th>
                </tr>
              </thead>
              <tbody>
                {(mergedDoc.results || []).map((r, idx) => {
                  const bgBase = idx % 2 === 0 ? "#fff" : "#fafafa";
                  const jIds = mergedDoc.judgeIds || [];
                  const rounds = [...(r.rounds || [])].sort((a, b) => {
                    function rOrd(r) { if (r === "F") return 10000; if (r === "Preliminary") return -1; const f = { "1/32 Final": 10, "1/16 Final": 20, "1/8 Final": 30, "1/4 Final": 40, "1/2 Final": 50 }; if (f[r] != null) return f[r]; const n = parseInt(r); return isNaN(n) ? 5 : n; }
                    return rOrd(b.round) - rOrd(a.round);
                  });
                  return rounds.map((rd, rdIdx) => {
                    const isFirstRound = rdIdx === 0;
                    const isFinalRound = rd.round === "F";
                    const rowBg = isFinalRound ? "#f0fff0" : bgBase;
                    return (
                      <tr key={`${idx}-${rdIdx}`} style={{ background: rowBg, borderTop: isFirstRound ? "2px solid #ccc" : undefined }}>
                        {isFirstRound && (
                          <>
                            <td style={{ ...tdStyle, fontWeight: "bold" }} rowSpan={rounds.length}>{r.place}</td>
                            <td style={tdStyle} rowSpan={rounds.length}>{r.number}</td>
                            <td style={tdStyle} rowSpan={rounds.length}><strong>{r.dancerName}</strong></td>
                            <td style={{ ...tdStyle, fontSize: "0.78rem", color: "#555" }} rowSpan={rounds.length}>{r.studio}</td>
                          </>
                        )}
                        <td style={{ ...tdStyle, fontWeight: isFinalRound ? "bold" : "normal", color: isFinalRound ? "#1a7a1a" : "#555", background: rowBg }}>
                          {rd.round === "F" ? "Final" : rd.round}
                        </td>
                        {(mergedDoc.dances || []).map(d => {
                          const dd = rd.danceData?.[d];
                          if (!dd) return (
                            <React.Fragment key={d}>
                              <td style={{ ...tdStyle, color: "#bbb", background: rowBg }}>—</td>
                              <td style={{ ...tdStyle, color: "#bbb", background: rowBg }}>—</td>
                            </React.Fragment>
                          );
                          const isFinal = dd.type === "final";
                          const sumDisplay = isFinal ? (dd.sum != null ? String(dd.sum) : "") : (dd.totalCrosses != null ? String(dd.totalCrosses) : "0");
                          return (
                            <React.Fragment key={d}>
                              <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.76rem", background: rowBg, whiteSpace: "pre", padding: "4px 6px" }}>
                                {jIds.map(jId => {
                                  let mark;
                                  if (isFinal) {
                                    const m = dd.judgeMarks?.[jId];
                                    mark = m != null ? String(m) : "·";
                                  } else {
                                    const rc = dd.judgeRecalls?.[jId];
                                    mark = rc === true ? "x" : "·";
                                  }
                                  const isHov = hoveredJudge === jId;
                                  const isSel = selectedJudges.has(jId);
                                  let bg = "transparent", fg = "inherit";
                                  if (isHov && isSel) { bg = "#ffb347"; fg = "#111"; }
                                  else if (isSel)     { bg = "#bbdefb"; fg = "#0d47a1"; }
                                  else if (isHov)     { bg = "#ffe066"; fg = "#222"; }
                                  return (
                                    <span key={jId} onMouseEnter={() => setHoveredJudge(jId)} onMouseLeave={() => setHoveredJudge(null)} onClick={() => toggleJudge(jId)} title={(mergedDoc.adjudicators || []).find(a => a.id === jId)?.name || jId}
                                      style={{ display: "inline-block", width: "1.6ch", textAlign: "center", background: bg, color: fg, borderRadius: "2px", fontWeight: (isHov || isSel) ? "bold" : "normal", transition: "background 0.1s", cursor: "pointer", userSelect: "none" }}>
                                      {mark}
                                    </span>
                                  );
                                })}
                              </td>
                              <td style={{ ...tdStyle, fontFamily: "monospace", fontWeight: "bold", background: rowBg, textAlign: "center", padding: "2px 4px" }}>
                                {sumDisplay}
                              </td>
                            </React.Fragment>
                          );
                        })}
                        {isFirstRound && <td style={tdStyle} rowSpan={rounds.length}>{r.points}</td>}
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          <div className="form-group">
            <label>📝 JSON (editable before upload):</label>
            <textarea rows={16} className="code-textarea" value={jsonPreview} onChange={e => setJsonPreview(e.target.value)} style={{ fontFamily: "monospace", fontSize: "0.78rem" }} />
          </div>
        </div>
      )}

      {mergedDoc && mergedDoc.discipline === "Standard" && (
        <div style={{ marginTop: "1rem", padding: "0.9rem 1.2rem", background: "#fff3cd", border: "1px solid #ffc107", borderRadius: "6px", color: "#856404", fontSize: "0.9rem" }}>
          ⚠️ <strong>Standard dances are not supported by this app's rankings.</strong><br />
          The discipline detected is <strong>"Standard"</strong> (Slow Waltz, Tango, Viennese Waltz, Slow Foxtrot, Quickstep).
          Standard dances will not appear in the Leaderboard or Star Rankings — only Latin, Contemporary, Hip Hop and Commercial are tracked.
          If this is a Latin competition, use the <em>Dance override</em> field above to set the correct dance name.
        </div>
      )}

      {jsonPreview && (
        <button className="btn btn-success btn-upload" onClick={handleUpload} style={{ marginTop: "1rem" }}>
          ☁️ Upload to Firebase
        </button>
      )}

      {uploadStatus && (
        <div className="upload-status" style={{ whiteSpace: "pre-wrap", marginTop: "1rem" }}>
          {uploadStatus}
        </div>
      )}
    </div>
  );
}

const thStyle = { padding: "5px 8px", border: "1px solid #ccc", textAlign: "left", fontWeight: "600", whiteSpace: "nowrap" };
const tdStyle = { padding: "4px 8px", border: "1px solid #e0e0e0", whiteSpace: "nowrap" };
